// # ae-list-selector Component Documentation

// This documentation provides a guide on how to use the `ae-list-selector` component and a detailed reference for its props.

// The `ae-list-selector` is a powerful data-fetching and rendering component. It acts as a data provider that can either query a remote source for a list of documents or work with a pre-existing array of documents or ids. It then passes the resulting data to a dynamically rendered list component, which is determined by the `listId` prop.

// ## How to Use

// The component has two primary modes of operation: **Query Mode** (fetching remote data) and **Static Mode** (using a provided array of documents or ids).

// ### Querying Data from a Remote Source

// This mode is active when the `documents` prop is **not** provided. You use either the `search` or `queryId` prop to define what data to fetch.
// The `queryId` also support defining its `root-document` and the queryType using `type`.

// **Example 1: Live-updating search results**
// This example searches for all documents of form `task` and will automatically update if the search results change on the server or if a document in the results is modified.

// ```html
// <ae-list-selector
//   listId="some UUID"
//   search="f.id:task"
//   :watchDocuments="true"></ae-list-selector>
// ```

// **Example 2: One-time query**
// This fetches the initial search results but will not update if new documents match the query later. It's useful for static lists.

// ```html
// <ae-list-selector
//   listId="some UUID"
//   search="data.name: TestName AND f.id:some-uuid"
//   :queryOnce="true"></ae-list-selector>
// ```

// **Example 3: One-time query with updates**
// This fetches the initial search results and will not update the documents found initially. This can be used when you dont want documents to disappearing after a user interaction. In this example if a task `erledigt` field is set to `true`, the task will remain in the list.

// ```html
// <ae-list-selector
//   listId="some UUID"
//   search="f.id:task erledig:false "
//   :queryOnce="true"
//   :watchDocuments="true"></ae-list-selector>
// ```

// **Example 4: Query using queryId**
// You can query using a `queryId`.

// ```html
// <ae-list-selector
//   listId="some UUID"
//   queryId="some UUID"
//   rootDocumentId="some UUID"
//   :type="'children' || 'descendants' || 'ancestors' || 'siblings'"
//   :queryOnce="false"
//   :watchDocuments="false"></ae-list-selector>
// ```

// ### Displaying a static List of Documents

// This mode is active when you pass an array to the `documents` prop. This is useful when the parent component already knows which documents it wants to display.

// **Example 1: Displaying a static list and watching for changes**
// This example will display the documents from `myDocumentArray` and will automatically update the list if any of those specific documents are changed elsewhere in the application.

// ```html
// <ae-list-selector
//   listId="some UUID"
//   :documents="myDocumentArray"
//   :watchDocuments="true"></ae-list-selector>
// ```

// **Example 2: Displaying a static list**
// This will display the documents exactly as they are provided and will not watch them for updates. If ids or references are provided, the newest version is fetched, but not updated.

// ```html
// <ae-list-selector
//   listId="ui-archived-items"
//   :documents="archivedItems"></ae-list-selector>
// ```

// ## Props API ⚙️

// Here is a detailed breakdown of the component's props.

// #### ### Core Props

// - **`listId`** (String, **required**)
//   The ID of a "list document" that contains the template and logic for rendering the actual list UI.

// - **`search`** (String)
//   A search string used to query for documents. Mutually exclusive with `queryId` and `documents`.

// - **`queryId`** (String)
//   The ID of a predefined query document. An alternative to using `search`. Mutually exclusive with `search` and `documents`.

// - **`documents`** (Array)
//   An array of documents, document references, or document IDs to display. Using this prop activates Static Mode. Mutually exclusive with `search` and `queryId`.

// #### ### Behavior Modifiers

// - **`watchDocuments`** (Boolean, default: `false`)
//   Controls live updates for individual documents in the list.

//   - In **Query Mode**, it watches the documents returned by the search. If a document changes, the list updates.

//   - In **Static Mode**, it watches the documents you provided in the `documents` prop.

// - **`queryOnce`** (Boolean, default: `false`)
//   Only applicable in **Query Mode**. If `true`, the component performs an initial search but does not subscribe to changes. The list will not update if documents are added or removed from the query results on the server.

// - **`suspend`** (Boolean, default: `false`)
//   If `true`, the component will stop all updates, subscriptions, and watchers, effectively freezing the list in its current state.

// #### ### Filtering and Sorting

// - **`filter`** (String)
//   A filter string that is applied **remotely** as part of the database query in Query Mode. In Static Mode it is applied locally.

// - **`localFilter`** (String)
//   A filter string that is applied **locally** to the documents _after_ they have been fetched.

// - **`sort`** (String | Object)
//   Defines the sort order.

//   - In **Query Mode**, this is applied remotely by the database.

//   - In **Static Mode**, this is applied locally to the provided `documents` array.

// - **`searchOptions`** (Object)
//   Allows passing advanced search options to the query, such as `{ size: 50 }`.

// #### ### Other Props

// - **`type`** (String)
//   Defines the type of a provided `queryId`

// - **`params`** (Object)
//   An object of parameters that will be passed down directly to the dynamic list component (defined by `listId`).

// - **`rootDocumentId`** (String, default: `'root'`)
//   Provides a context document for the queryId.

// - **`debug`** (Boolean, default: `false`)
//   Enables verbose console logging for debugging purposes.

// ## Emitted Events

// The component emits events when its internal state changes, allowing parent components to react.

// - **`ae-documents-changed`**
//   Fires whenever the final list of documents to be rendered is changed. The event payload is the array of documents.

// - **`ae-filter-changed`**
//   Fires when the combined local and remote filter string changes.

// - **`ae-search-changed`**
//   Fires when the final search options object (including sorting and page size) changes.

import DynamicComponent from '../dynamic/dynamic-component'
import createEventForwarders from '../utils/create-event-forwarders'

import buildCompleteSearchString from '../utils/build-query-string'
import { isValidTypeOrNull } from '../utils/build-query-string'
import { isReference, isDocument } from '../../../src/model/is.js'
import { readId } from './../../model/id.js'
import { Types } from 'index.js'

function haveSameIdsInOrder(documentsA: Types.Document[], documentsB: Types.Document[]): Boolean {
  if (documentsA.length !== documentsB.length) {
    return false
  }

  const unchangedIds = documentsA.every((docA, index) => docA.id === documentsB[index].id)

  return unchangedIds
}

function combineFiltersWithAnd(...filters: string[]): string {
  if (filters.length === 0) {
    return null
  }

  return filters
    .map((filter) => String(filter ?? '').trim()) // Map everything onto a string. Trim them
    .filter(Boolean) // Remove empty strings
    .map((filter) => `(${filter})`) // Add parenthesis only around non empty strings. Parenthesis are needed because of order of operation.
    .join(' AND ')
}

enum modeType {
  suspended = 'suspended',
  query = 'query',
  static = 'static',
}

export default {
  /* tslint:disable */
  render() {
    const _vm = this
    const _h = _vm.$createElement
    const _c = _vm._self._c || _h

    return _vm.setupDone && _vm.dynamicComponent
      ? _c(_vm.dynamicComponent.id, {
          tag: 'component',
          key: this.componentKey,
          props: {
            Aeppic: _vm.Aeppic,
            params: _vm.params,
            list: _vm.dynamicComponent.document,
            documents: _vm.documentsToBindTo,
            rootDocument: _vm.rootDocument,
            queryDocument: _vm.queryDocument,
            searchOptions: _vm.combinedSearchOptions,
            filter: _vm.combinedInternalRemoteAndLocalFilter,
            suspended: _vm.listSuspended,
            suspendList: _vm.suspendList,
            unsuspendList: _vm.unsuspendList,
            style: 'width: 100%',
          },
          on: createEventForwarders(_vm._events, _vm),
        })
      : _vm._e()
  },
  renderError(h, err) {
    return h('pre', { staticClass: 'ae-error', style: { color: 'red' } }, 'ae-list:' + this.listId + ' ' + err.stack)
  },
  staticRenderFns: [],
  /* tslint:enable */
  inject: ['getAeppicContext'],
  props: {
    listId: {
      type: String,
      required: true,
    },
    params: Object,
    search: String,
    queryOnce: {
      type: Boolean,
      default: false,
    },
    sort: null,
    searchOptions: Object,
    queryId: String,
    filter: String,
    localFilter: String,
    rootDocumentId: {
      type: String,
      default: 'root',
    },
    documents: Array,
    watchList: {
      type: Boolean,
      default: null,
    },
    watchListRevisions: {
      type: Boolean,
      default: null,
    },
    watchDocuments: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      validator: isValidTypeOrNull,
    },
    suspend: {
      type: Boolean,
      default: false,
    },
    debug: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const Aeppic = this.getAeppicContext('ae-list-selector:-')

    const isDeveloper = Aeppic.Account && Aeppic.Account.data.allowDevMode

    const watch = typeof this.watchList === 'boolean' ? this.watchList : isDeveloper
    const watchRevisions = Aeppic.Developer.isServerInRemoteDeveloperMode ? false : typeof this.watchControlRevisions === 'boolean' ? this.watchControlRevisions : isDeveloper

    const dynamicComponent = new DynamicComponent('list', Aeppic, {
      formId: 'list-form',
      template: LIST_COMPONENT_TEMPLATE,
      exposedSymbols:
        'locals, Aeppic, Math, DateTime, Anime, params, watchParam, $el, $refs, $listeners, list, documents, $emit, $on, rootDocument, setSorting, setSize, setSearchOptions, queryDocument, searchOptions, setFilter, filter, suspended, suspendList, unsuspendList, SharedContext, isLoading',
      watch,
      watchRevisions,
    })

    return {
      Aeppic,
      dynamicComponent,
      subscription: null,
      documentsWatcher: null,
      rootDocument: null,
      queryDocument: null,
      internalDocumentsBeforeFilter: null,
      internalDocumentsAfterFilter: [],
      manualSearchSize: null,
      manualSearchSort: null,
      manualSearchOptions: null,
      manualFilter: null,
      setupDone: false,
      listSuspended: false,
      documentsToBindTo: [],
      isLoading: false,
      setupModeExecutionId: 0,
    }
  },
  mounted() {
    this.Aeppic.setContextRootElement(this.$el)
  },
  updated() {
    this.Aeppic.setContextRootElement(this.$el)
  },
  async created() {
    this.debug && console.log('ae-list-selector created')
    this.componentKey = this.Aeppic.uuid()
    this.listSuspended = this.suspend || false

    this.dynamicComponent.on('invalidated', () => this.loadListComponent())

    const [queryDocument, rootDocument] = await this.Aeppic.getAll([this.queryId, this.rootDocumentId])

    this.queryDocument = queryDocument
    this.rootDocument = rootDocument

    this.loadListComponent()

    this.setupDone = true
  },
  computed: {
    externalDocumentsFingerprint() {
      // This does not trigger when just changing a field in an object in this.documents. The array needs to be replaced.
      // To achieve the deep watching, add a watcher for this.documents with deep watching enabled, that sets externalDocumentsFingerprint
      if (!this.documents) {
        return null
      }

      const fingerprintString = this.documents.map((document) => `id:${document.id},v:${document.v}`).join('|')
      return fingerprintString
    },
    mode() {
      if (this.listSuspended) {
        return modeType.suspended
      }

      if (this.documents) {
        return modeType.static
      }

      return modeType.query
    },
    queryState() {
      if (this.mode === modeType.suspended) {
        return 'suspended'
      }
      if (this.mode === modeType.static) {
        return `documents:${this.externalDocumentsFingerprint}|watch:${this.watchDocuments}`
      }
      if (this.mode === modeType.query) {
        return `query:${this.searchString}|combinedSearchOptions:${JSON.stringify({ sort: this.combinedSearchOptions.sort, size: this.combinedSearchOptions.size })}|watchDocuments:${
          this.watchDocuments
        }|queryOnce:${this.queryOnce}|internalRemoteFilter:${this.internalRemoteFilter}`
      }
      return null
    },
    internalDocumentsAfterFilterFingerprint() {
      // Stamps are not detected my subscriptions but are by watchers.
      // Because deep watching this.documents is turned off, version changes created by committing an editable document are not detected.

      const documentToString = (document: Types.Document) => {
        const stampString = (document.stamps?.map((stamp) => `stamp:${stamp.id},v:${stamp.v}`) ?? []).join(',')
        return `id:${document.id},v:${document.v},${stampString}`
      }
      const fingerprintString = this.internalDocumentsAfterFilter.map((document) => documentToString(document)).join('|')
      this.debug && console.log('internalDocumentsAfterFilterFingerprint', fingerprintString)
      return fingerprintString
    },
    combinedSearchOptions() {
      const sort = this.computedSearchSort
      const size = this.computedSearchSize
      const tag = this.listId
      return {
        ...this.searchOptions,
        ...this.manualSearchOptions,
        ...(sort != null && { sort }),
        ...(size != null && { size }),
        ...(tag != null && { tag }),
      }
    },
    computedSearchSize() {
      const calculatedSearchSize = (this.searchOptions && this.searchOptions.size) || (this.queryDocument && this.queryDocument.data.pageSize)
      const searchSize = this.manualSearchSize ?? calculatedSearchSize

      if (Number.isNaN(searchSize)) {
        return null
      }

      return searchSize
    },
    computedSearchSort() {
      return this.manualSearchSort || this.sort || null
    },
    searchString(): string {
      return buildCompleteSearchString(this.search, this.internalRemoteFilter, this.type, this.rootDocument, this.queryDocument)
    },
    internalLocalFilter(): string {
      switch (this.mode) {
        case modeType.query: {
          return this.localFilter
        }
        case modeType.static: {
          return combineFiltersWithAnd(this.filter, this.localFilter, this.manualFilter)
        }
        default:
          return ''
      }
    },
    internalRemoteFilter(): string {
      switch (this.mode) {
        case modeType.query: {
          return combineFiltersWithAnd(this.filter, this.manualFilter)
        }
        default:
          return ''
      }
    },
    combinedInternalRemoteAndLocalFilter(): string {
      return combineFiltersWithAnd(this.internalRemoteFilter, this.internalLocalFilter)
    },
  },
  methods: {
    async setupMode() {
      this.setupModeExecutionId += 1
      const executionId = this.setupModeExecutionId
      const mayContinue = () => {
        return executionId === this.setupModeExecutionId
      }

      const setInternalDocuments = (changedDocuments) => {
        if (this.debug) {
          this.debug && console.log('May continue', mayContinue())
          this.debug && console.log(changedDocuments.map((doc) => `${doc.id},${doc.v}`).join(' | '))
        }
        if (!mayContinue()) {
          return
        }
        // Filter creates a shallow copy. Important because the subscription/watcher just changes the array without changing the reference.
        this.isLoading = false
        // Create a new array to trigger internalDocumentsAfterFilterFingerprint
        this.internalDocumentsBeforeFilter = changedDocuments.filter(Boolean)
      }

      const queryOnceAndWatchDocuments = async () => {
        const findResult = (await this.Aeppic.find(this.searchString, this.combinedSearchOptions)).filter(Boolean)
        if (!mayContinue()) {
          return
        }
        this.updateWatcher(this.Aeppic.watchAll(findResult, setInternalDocuments))
      }

      const queryOnceAndDontWatchDocuments = async () => {
        const findResult = await this.Aeppic.find(this.searchString, this.combinedSearchOptions)
        if (!mayContinue()) {
          return
        }
        setInternalDocuments(findResult)
      }

      const dontQueryOnceAndWatchDocuments = async () => {
        // Needed, because subscriptions do not have an initial callback
        const newDocuments = await this.Aeppic.find(this.searchString, this.combinedSearchOptions)
        if (!mayContinue()) {
          return
        }
        this.updateWatcher(this.Aeppic.watchAll(newDocuments, setInternalDocuments))
        this.updateSubscription(
          this.Aeppic.subscribe(this.searchString, this.combinedSearchOptions, (documents) => {
            const internalDocumentsBeforeFilterAreInitialized = this.internalDocumentsBeforeFilter !== null
            const mustUpdateSubscription = internalDocumentsBeforeFilterAreInitialized ? !haveSameIdsInOrder(this.internalDocumentsBeforeFilter, documents) : true

            if (!mustUpdateSubscription) {
              // This change is handled by the documents watcher.
              return
            }

            this.updateWatcher(this.Aeppic.watchAll(documents, setInternalDocuments))
          })
        )
      }

      const dontQueryOnceAndDontWatchDocuments = async () => {
        // Needed, because subscriptions do not have an initial callback
        const findResult = await this.Aeppic.find(this.searchString, this.combinedSearchOptions)
        if (!mayContinue()) {
          return
        }
        setInternalDocuments(findResult)
        this.updateSubscription(this.Aeppic.subscribe(this.searchString, this.combinedSearchOptions, setInternalDocuments))
      }

      this.stopWatcherAndSubscription()
      switch (this.mode) {
        case modeType.suspended: {
          this.isLoading = false
          break
        }
        case modeType.query: {
          this.isLoading = true
          if (this.queryOnce && this.watchDocuments) {
            queryOnceAndWatchDocuments()
          } else if (this.queryOnce && !this.watchDocuments) {
            queryOnceAndDontWatchDocuments()
          } else if (!this.queryOnce && this.watchDocuments) {
            dontQueryOnceAndWatchDocuments()
          } else if (!this.queryOnce && !this.watchDocuments) {
            dontQueryOnceAndDontWatchDocuments()
          }
          break
        }
        case modeType.static: {
          this.isLoading = true
          if (this.watchDocuments) {
            const documentIds = this.documents.map((doc) => readId(doc)).filter(Boolean)
            this.updateWatcher(this.Aeppic.watchAll(documentIds, setInternalDocuments))
          } else {
            setInternalDocuments(
              await Promise.all(
                this.documents.map(async (doc) => {
                  if (isDocument(doc)) {
                    // Don't change the document
                    return doc
                  } else if (isReference(doc) || typeof doc === 'string') {
                    // NOTE: A match cannot be determined without accessing the document. It might be deleted or locked without access. Current behavior is to render it anyway.
                    const document = await this.Aeppic.get(doc)
                    if (!document) {
                      return doc
                    }

                    return document
                  } else {
                    return null
                  }
                })
              )
            )
          }
          break
        }
        default:
          console.warn('Unknown mode: ', this.mode)
          break
      }
    },
    async setupSearch() {
      this.verifyProperties()

      switch (this.mode) {
        case modeType.suspended: {
          // Keep documentsToBindTo the same
          break
        }
        case modeType.query: {
          const locallyFilteredDocuments: Types.Document[] = await this.filterDocuments(this.internalDocumentsBeforeFilter, this.internalLocalFilter)
          this.internalDocumentsAfterFilter = locallyFilteredDocuments
          break
        }
        case modeType.static: {
          const locallyFilteredDocuments: Types.Document[] = await this.filterDocuments(this.internalDocumentsBeforeFilter, this.internalLocalFilter, this.combinedSearchOptions)
          const sortedDocuments: Types.Document[] = this.sortDocuments(locallyFilteredDocuments, this.combinedSearchOptions.sort)
          this.internalDocumentsAfterFilter = sortedDocuments
          break
        }
      }
    },
    teardownDocumentsWatchers() {
      if (this.documentsWatcher) {
        this.documentsWatcher.stop()
        this.documentsWatcher = null
      }
    },
    teardownSubscription() {
      if (this.subscription) {
        this.subscription.cancel()
        this.subscription = null
      }
    },
    stopWatcherAndSubscription() {
      this.teardownDocumentsWatchers()
      this.teardownSubscription()
    },
    verifyProperties() {
      if (+!!this.search + +!!this.queryId + +!!this.documents > 1) {
        /* tslint:disable:no-console */
        console.warn(`[WARN]: Invalid prop: Do not mix "search", "queryId" and/or "documents".`)
      }
      if (this.mode === modeType.static && this.queryOnce) {
        /* tslint:disable:no-console */
        console.warn(`[WARN]: Invalid prop: queryOnce can only be used in combination with search or queryId.`)
      }
    },
    async loadListComponent() {
      const listDocument = this.listId ? await this.Aeppic.get(this.listId) : null

      if (listDocument) {
        this.Aeppic.renameContext(`ae-list-selector:${this.listId}:${listDocument?.data.name ?? '?'}`)
        this.dynamicComponent.refresh(listDocument)
      } else {
        this.Aeppic.renameContext(`ae-list-selector:${this.listId}:-`)
        this.dynamicComponent.reset()
      }
    },
    emitDynamicComponent(name, value) {
      this.$emit(name, value)

      // FIXME: somehow this is not needed anymore. With using this, the event is emitted twice!
      //   // children[0] === dynamicComponent
      //   if (this.$children[0]) {
      //     // side-effect: this.$emit is called by eventForwarder applied to dynamicComponent
      //     this.$children[0].$emit(name, value)
      //   }
    },
    suspendList() {
      this.listSuspended = true
    },
    unsuspendList() {
      this.listSuspended = false
    },
    beforeDestroy() {
      this.stopWatcherAndSubscription()
      this.dynamicComponent.destroy()
      this.Aeppic.release()
    },
    async filterDocuments(documents: Types.Document[], filter: string, filterOptions?): Promise<Types.Document[]> {
      if (!documents) {
        return []
      }

      const matchResults = await Promise.all(
        documents.map(async (document) => {
          // NOTE: A match cannot be determined without accessing the document. It might be deleted or locked without access. Current behavior is to render it anyway.
          if ('data' in document === false) {
            return true
          }

          return this.Aeppic.match(document, filter, filterOptions)
        })
      )
      const matchedDocuments = documents.filter((doc, index) => matchResults[index])
      return matchedDocuments
    },
    sortDocuments(documents: Types.Document[], sortOptions) {
      return this.Aeppic.sort(documents, sortOptions)
    },
    updateWatcher(watcher) {
      // Necessary to ensure the old watcher is always stopped, before storing a new one
      if (this.documentsWatcher) {
        this.documentsWatcher.stop()
      }

      this.documentsWatcher = watcher
    },
    updateSubscription(subscription) {
      // Necessary to ensure the old subscription is always stopped, before storing a new one
      if (this.subscription) {
        this.subscription.cancel()
      }

      this.subscription = subscription
    },
  },
  watch: {
    queryState: {
      handler: 'setupMode',
      immediate: true,
    },
    internalDocumentsBeforeFilter: 'setupSearch',
    internalLocalFilter: 'setupSearch',
    listId: 'loadListComponent',
    suspend(isSuspended) {
      if (isSuspended === this.listSuspended) {
        return
      }

      if (isSuspended) {
        this.suspendList()
      } else {
        this.unsuspendList()
      }
    },
    async queryId(newValue) {
      // NOTE: Switching to this.queryId will lead to this.searchString being empty, because the queryDocument is gotten asynchronously.
      this.queryDocument = await this.Aeppic.get(newValue)
    },
    async rootDocumentId(newValue) {
      this.rootDocument = await this.Aeppic.get(newValue)
    },
    combinedSearchOptions(value) {
      this.setupSearch()
      this.emitDynamicComponent('ae-search-changed', value)
    },
    async combinedInternalRemoteAndLocalFilter(value) {
      this.emitDynamicComponent('ae-filter-changed', value)
    },
    internalDocumentsAfterFilterFingerprint() {
      this.documentsToBindTo = this.internalDocumentsAfterFilter

      if (this.setupDone) {
        this.emitDynamicComponent('ae-documents-changed', this.internalDocumentsAfterFilter)
      }
    },
    isLoading() {
      this.emitDynamicComponent('ae-loading', this.isLoading)
    },
  },
}

// FIXME: add $on-update event for controller after subscription change
const LIST_COMPONENT_TEMPLATE = `{
  template: __TEMPLATE__,
  inject: ['getAeppicContext'],
  props: ['Aeppic', 'params', 'list', 'documents', 'rootDocument', 'queryDocument', 'searchOptions', 'filter', 'suspended', 'suspendList', 'unsuspendList', 'isLoading'],
  methods: {
    __METHODS__,
    translate(...args) {
      return this.Aeppic.translate(...args)
    },
    watchParam(name, cb, options) {
      if (!name) {
        return
      }

      let lastValue = undefined

      const cbWithPreCheck = (newValue, oldValue) => {
        if (Array.isArray(newValue) || lastValue !== newValue) {
          cb(newValue, oldValue)
        }

        lastValue = newValue
      }
      
      return this.$watch('params.' + name, cbWithPreCheck, options)
    }
  },
  beforeCreate() {
    this.setSearchSize = (newValue) => {
      this.$parent.manualSearchSize = newValue
    }

    this.setSearchSort = (newValue) => {
      this.$parent.manualSearchSort = newValue
    }

    this.setSearchOptions = (newValue) => {
      this.$parent.manualSearchOptions = newValue
    }

    this.setFilter = (newValue) => {
      this.$parent.manualFilter = newValue
    }
  },
  data() {
    const Aeppic = this.Aeppic

    const self = this
    const locals = __NEW_LOCALS__
    const handlers = []

    let sharedContext = null

    const controllerVariables = {
      locals,
      Aeppic,
      get DateTime() { return Aeppic.DateTime },
      get Math() { return Aeppic.Math },
      get Anime() { return Aeppic.Anime },
      get params() { return self.params },
      get documents() { return self.documents },
      get list() { return self.list },
      get rootDocument() { return self.rootDocument },
      get queryDocument() { return self.queryDocument },
      get searchOptions() { return self.searchOptions },
      get filter() { return self.filter },
      get $el() { return self.$el },
      get $refs() { return self.$refs },
      $listeners: self.$listeners,
      $emit(...args) { return self.$emit(...args) },
      $on(...args) { 
        self.$parent.$on(...args)
        self.$on(...args)

        handlers.push(args)
      },
      get SharedContext() {
        if (!self.$el) {
          // console.error('SharedContext is only available after the component is mounted')
          // throw new Error('SharedContext is only available after the component is mounted')
          return {}
        }

        if (!self.$el.parentNode) {
          // console.error('SharedContext is only available after the component is mounted and has a parent node')
          // throw new Error('SharedContext is only available after the component is mounted and has a parent node')
          return {}
        }

        if (!sharedContext) {
          sharedContext = Aeppic.SharedContext.forElement(self.$el)
        }

        return sharedContext
      },
      watchParam(...args) { return self.watchParam(...args) },
      setSize(value) { self.setSearchSize(value) },
      setSorting(value) { self.setSearchSort(value) },
      setSearchOptions(value) { self.setSearchOptions(value) },
      setFilter(value) { self.setFilter(value) },
      suspendList() { self.suspendList() },
      unsuspendList() { self.unsuspendList() },
      get suspended() { return self.suspended },
      get isLoading() { return self.isLoading },
    }

    const controller = controllerClass ? new controllerClass(controllerVariables) : null

    return {
      controller,
      locals,
      packages,
      handlers
    }
  },
  computed: {
    Math() { return this.Aeppic.Math },
    DateTime() { return this.Aeppic.DateTime },
    Anime() { return this.Aeppic.Anime },
  },
  beforeDestroy() {
    __DESTROY_CONTROLLER__

    for (const handlerArgs of this.handlers) {
      this.$off(...handlerArgs) // remove all controller registered listeners (own scope)
      this.$parent.$off(...handlerArgs) // remove all controller registered listeners (parent scope)
    }

    this.handlers = []
    this.packages = null
  },
  mounted() {
    __MOUNT_CONTROLLER__
  },
  components
}`
